/**
 * Lite-mode catalog — section / card / action codes and landing order.
 * Keep in sync with PatientAdminMUI/src/config/liteModeCatalog.ts.
 *
 * When LiteModeEnabled is on, a missing section is hidden. Account and logout are never gated.
 */

export const DEFAULT_LITE_MODE_SECTIONS_JSON =
  '{"sections":{"exams":{"enabled":true,"cards":{"summary":true,"report":true,"images":true},"actions":{"share":false,"downloadImages":true}},"messages":{"enabled":true},"help":{"enabled":true}}}';

export interface LiteModeCardDef {
  code: string;
  label: string;
}

export interface LiteModeActionDef {
  code: string;
  label: string;
}

export interface LiteModeSectionDef {
  code: string;
  label: string;
  route: string;
  routeAliases?: readonly string[];
  cards?: readonly LiteModeCardDef[];
  actions?: readonly LiteModeActionDef[];
}

export const LITE_MODE_SECTIONS: readonly LiteModeSectionDef[] = [
  { code: 'dashboard', label: 'Dashboard', route: '/dashboard' },
  {
    code: 'exams',
    label: 'Exams',
    route: '/exams',
    cards: [
      { code: 'summary', label: 'Exam Summary' },
      { code: 'report', label: 'Report' },
      { code: 'images', label: 'Images' },
      { code: 'attachments', label: 'Attachments' },
      { code: 'ccda', label: 'CCDA' },
      { code: 'activity', label: 'Activity' },
      { code: 'layletter', label: 'Lay Letter' },
      { code: 'gfe', label: 'Good Faith Estimate' },
    ],
    actions: [
      { code: 'share', label: 'Share' },
      { code: 'downloadImages', label: 'Download Images' },
    ],
  },
  {
    code: 'appointments',
    label: 'Appointments',
    route: '/appointments',
    routeAliases: ['/schedule', '/gfe'],
    cards: [
      { code: 'summary', label: 'Appointment Summary' },
      { code: 'prep', label: 'Prep' },
      { code: 'registration', label: 'Registration' },
      { code: 'attachments', label: 'Attachments' },
      { code: 'gfe', label: 'Good Faith Estimate' },
    ],
    actions: [
      { code: 'schedule', label: 'Schedule' },
      { code: 'reschedule', label: 'Reschedule' },
      { code: 'cancel', label: 'Cancel' },
    ],
  },
  { code: 'messages', label: 'Messages', route: '/messages' },
  { code: 'help', label: 'Get Help', route: '/help' },
  { code: 'share', label: 'Share', route: '/share' },
  { code: 'aria', label: 'Aria', route: '/dashboard-ai' },
] as const;

const LITE_MODE_LEGACY_ROUTES: ReadonlyArray<{ code: string; route: string }> = [
  { code: 'healthSummary', route: '/health-summary' },
];

export const LITE_MODE_ALWAYS_ALLOWED_ROUTES = ['/account'] as const;

export interface LiteModeSectionConfig {
  enabled?: boolean;
  cards?: Record<string, boolean>;
  actions?: Record<string, boolean>;
}

export interface LiteModeSectionsConfig {
  sections?: Record<string, LiteModeSectionConfig>;
}

export function normalizeLiteCode(code: string): string {
  return code.trim().toLowerCase();
}

export function findSectionConfig(
  tree: LiteModeSectionsConfig,
  sectionCode: string,
): LiteModeSectionConfig | undefined {
  const sections = tree.sections;
  if (!sections) return undefined;
  const want = normalizeLiteCode(sectionCode);
  for (const [key, value] of Object.entries(sections)) {
    if (normalizeLiteCode(key) === want) return value;
  }
  return undefined;
}

function findFlag(map: Record<string, boolean> | undefined, code: string): boolean | undefined {
  if (!map) return undefined;
  const want = normalizeLiteCode(code);
  for (const [key, value] of Object.entries(map)) {
    if (normalizeLiteCode(key) === want) return value;
  }
  return undefined;
}

export function parseLiteModeSections(raw: string | undefined | null): LiteModeSectionsConfig {
  if (raw == null || raw.trim() === '') {
    return JSON.parse(DEFAULT_LITE_MODE_SECTIONS_JSON) as LiteModeSectionsConfig;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return JSON.parse(DEFAULT_LITE_MODE_SECTIONS_JSON) as LiteModeSectionsConfig;
    }
    return parsed as LiteModeSectionsConfig;
  } catch {
    return JSON.parse(DEFAULT_LITE_MODE_SECTIONS_JSON) as LiteModeSectionsConfig;
  }
}

export function isLiteSectionEnabled(tree: LiteModeSectionsConfig, sectionCode: string): boolean {
  return findSectionConfig(tree, sectionCode)?.enabled === true;
}

export function isLiteCardVisible(
  tree: LiteModeSectionsConfig,
  sectionCode: string,
  cardCode: string,
): boolean {
  const section = findSectionConfig(tree, sectionCode);
  if (section?.enabled !== true) return false;
  if (section.cards === undefined) return true;
  return findFlag(section.cards, cardCode) === true;
}

export function isLiteActionVisible(
  tree: LiteModeSectionsConfig,
  sectionCode: string,
  actionCode: string,
): boolean {
  const section = findSectionConfig(tree, sectionCode);
  if (section?.enabled !== true) return false;
  if (section.actions === undefined) return true;
  return findFlag(section.actions, actionCode) === true;
}

export function resolveSectionForPath(pathname: string): string | null {
  const first = `/${pathname.split('/').filter(Boolean)[0] ?? ''}`.toLowerCase();
  if (!first || first === '/') return null;

  for (const section of LITE_MODE_SECTIONS) {
    if (section.route.toLowerCase() === first) return section.code;
    if (section.routeAliases?.some((alias) => alias.toLowerCase() === first)) return section.code;
  }
  for (const legacy of LITE_MODE_LEGACY_ROUTES) {
    if (legacy.route.toLowerCase() === first) return legacy.code;
  }
  return null;
}

export function resolveLiteLandingPath(tree: LiteModeSectionsConfig): string {
  for (const section of LITE_MODE_SECTIONS) {
    if (isLiteSectionEnabled(tree, section.code)) return section.route;
  }
  return '/account';
}

export function isLitePathAllowed(tree: LiteModeSectionsConfig, pathname: string): boolean {
  const first = `/${pathname.split('/').filter(Boolean)[0] ?? ''}`.toLowerCase();
  if (LITE_MODE_ALWAYS_ALLOWED_ROUTES.some((route) => route === first)) return true;
  const sectionCode = resolveSectionForPath(pathname);
  if (!sectionCode) return true;
  return isLiteSectionEnabled(tree, sectionCode);
}
